import React from "react";
import { Collapse, Tag } from "antd";
import { useTranslation } from "react-i18next";

import { intentLabelToStackedBarRgba } from "constants/dashboardIntentColors";
import { translateIntentLabel } from "util/analysisLabelI18n";
import styles from "./style.module.scss";

function dedupeRows(rows, keyFn) {
  const seen = new Map();
  for (const row of rows || []) {
    const key = keyFn(row);
    if (!key || seen.has(key)) continue;
    seen.set(key, row);
  }
  return [...seen.values()];
}

export function aggregateReviewAnalysis(items) {
  const rootCauses = [];
  const keyDrivers = [];
  const highlights = [];
  const topics = [];
  const intentLabels = [];
  let summary = null;

  for (const item of items || []) {
    const analysis = item?.review_analysis;
    if (!analysis) continue;
    if (!summary && analysis.summary) summary = analysis.summary;
    if (analysis.intent) intentLabels.push(analysis.intent);
    if (analysis.aggregated_intent?.counts) {
      Object.entries(analysis.aggregated_intent.counts).forEach(([label, count]) => {
        for (let i = 0; i < count; i += 1) intentLabels.push(label);
      });
    }
    rootCauses.push(...(analysis.root_causes || []));
    keyDrivers.push(...(analysis.key_drivers || []));
    highlights.push(...(analysis.highlights || []));
    topics.push(...(analysis.topics || []));
  }

  const aggregatedIntent = aggregateIntentFromLabels(intentLabels);

  return {
    summary,
    aggregated_intent: aggregatedIntent,
    root_causes: dedupeRows(
      rootCauses,
      (row) => row.rc_id || `${row.root_cause}|${row.recommendation}`
    ),
    key_drivers: dedupeRows(
      keyDrivers,
      (row) => `${row.finding}|${row.topic}|${row.widget_id}`
    ),
    highlights: dedupeRows(
      highlights,
      (row) =>
        row.slice_id ||
        `${row.title}|${row.theme}|${row.segment}|${row.widget_id}`
    ),
    topics: dedupeRows(topics, (row) => row.id || row.label),
  };
}

function aggregateIntentFromLabels(labels) {
  const counts = {};
  for (const label of labels || []) {
    if (!label) continue;
    counts[label] = (counts[label] || 0) + 1;
  }
  const total = Object.values(counts).reduce((sum, n) => sum + n, 0);
  const percentages = Object.fromEntries(
    Object.entries(counts).map(([key, count]) => [
      key,
      total ? Math.round((count / total) * 100) : 0,
    ])
  );
  const dominant =
    total > 0
      ? Object.keys(counts).sort(
          (a, b) => counts[b] - counts[a] || a.localeCompare(b)
        )[0]
      : null;
  return { counts, percentages, total, dominant };
}

function IntentBreakdown({ agg, t }) {
  if (!agg?.total) return null;
  const entries = Object.entries(agg.counts || {})
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1]);

  return (
    <AnalysisBlock title={t("customer_365.analysis_intent", "Intent")}>
      <div className={styles.intentAgg}>
        <div className={styles.sentimentAggBar}>
          {entries.map(([label]) => {
            const pct = agg.percentages?.[label] || 0;
            if (!pct) return null;
            return (
              <span
                key={label}
                className={styles.intentBarSegment}
                style={{
                  width: `${pct}%`,
                  backgroundColor: intentLabelToStackedBarRgba(label),
                }}
              />
            );
          })}
        </div>
        <div className={styles.intentAggLabels}>
          {entries.map(([label]) => (
            <Tag
              key={label}
              style={{
                color: intentLabelToStackedBarRgba(label, 1),
                borderColor: intentLabelToStackedBarRgba(label, 0.35),
              }}
            >
              {translateIntentLabel(label, t)} {agg.percentages?.[label] ?? 0}%
            </Tag>
          ))}
        </div>
      </div>
    </AnalysisBlock>
  );
}

function AnalysisBlock({ title, children }) {
  if (!children) return null;
  return (
    <div className={styles.analysisBlock}>
      <div className={styles.analysisBlockTitle}>{title}</div>
      {children}
    </div>
  );
}

const ReviewAnalysisPanel = ({ analysis, compact = false }) => {
  const { t } = useTranslation();

  if (!analysis) return null;

  const {
    date_gpid: dateGpid,
    summary,
    aggregated_intent: aggregatedIntent,
    root_causes: rootCauses = [],
    key_drivers: keyDrivers = [],
    highlights = [],
    topics = [],
  } = analysis;

  const hasContent =
    summary ||
    aggregatedIntent?.total > 0 ||
    rootCauses.length > 0 ||
    keyDrivers.length > 0 ||
    highlights.length > 0 ||
    topics.length > 0;

  if (!hasContent) return null;

  const body = (
    <>
      {dateGpid ? (
        <div className={styles.reviewGpid}>GPID: {dateGpid}</div>
      ) : null}

      {summary ? (
        <AnalysisBlock title={t("customer_365.analysis_summary", "Summary")}>
          <div className={styles.analysisText}>{summary}</div>
        </AnalysisBlock>
      ) : null}

      <IntentBreakdown agg={aggregatedIntent} t={t} />

      {rootCauses.length > 0 ? (
        <AnalysisBlock title={t("customer_365.analysis_root_cause", "Root cause")}>
          <div className={styles.analysisList}>
            {rootCauses.map((row) => (
              <div
                key={row.rc_id || `${row.root_cause}-${row.recommendation}`}
                className={styles.analysisCard}
              >
                {row.label ? <Tag>{row.label}</Tag> : null}
                {row.root_cause ? (
                  <div className={styles.analysisText}>{row.root_cause}</div>
                ) : null}
                {row.recommendation ? (
                  <div className={styles.analysisMuted}>
                    {t("customer_365.analysis_recommendation", "Recommendation:")}{" "}
                    {row.recommendation}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </AnalysisBlock>
      ) : null}

      {keyDrivers.length > 0 ? (
        <AnalysisBlock title={t("customer_365.analysis_key_drivers", "Key drivers")}>
          <div className={styles.analysisList}>
            {keyDrivers.map((row) => (
              <div
                key={`${row.finding}-${row.topic}-${row.widget_id}`}
                className={styles.analysisCard}
              >
                {row.topic ? <Tag color="blue">{row.topic}</Tag> : null}
                {row.finding ? (
                  <div className={styles.analysisText}>{row.finding}</div>
                ) : null}
                {row.meaning ? (
                  <div className={styles.analysisMuted}>{row.meaning}</div>
                ) : null}
                {row.action ? (
                  <div className={styles.analysisAction}>
                    {t("customer_365.analysis_action", "Action:")} {row.action}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </AnalysisBlock>
      ) : null}

      {highlights.length > 0 ? (
        <AnalysisBlock title={t("customer_365.analysis_highlights", "Highlights")}>
          <div className={styles.analysisList}>
            {highlights.map((row) => (
              <div
                key={
                  row.slice_id ||
                  `${row.title}-${row.theme}-${row.segment}-${row.widget_id}`
                }
                className={styles.analysisCard}
              >
                <div className={styles.analysisTags}>
                  {row.theme ? <Tag>{row.theme}</Tag> : null}
                  {row.segment ? <Tag>{row.segment}</Tag> : null}
                </div>
                {row.title ? (
                  <div className={styles.analysisTextStrong}>{row.title}</div>
                ) : null}
                {row.description ? (
                  <div className={styles.analysisText}>{row.description}</div>
                ) : null}
                {row.meaning ? (
                  <div className={styles.analysisMuted}>{row.meaning}</div>
                ) : null}
                {row.action ? (
                  <div className={styles.analysisAction}>
                    {t("customer_365.analysis_action", "Action:")} {row.action}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </AnalysisBlock>
      ) : null}

      {topics.length > 0 ? (
        <AnalysisBlock title={t("customer_365.analysis_topics", "Topics")}>
          <div className={styles.reviewTopics}>
            {topics.map((topic) => (
              <Tag key={topic.id || topic.label}>{topic.label}</Tag>
            ))}
          </div>
        </AnalysisBlock>
      ) : null}
    </>
  );

  if (compact) {
    return <div className={styles.analysisPanel}>{body}</div>;
  }

  return (
    <Collapse
      ghost
      size="small"
      className={styles.analysisCollapse}
      items={[
        {
          key: "analysis",
          label: t("customer_365.pivony_analysis"),
          children: body,
        },
      ]}
    />
  );
};

export default ReviewAnalysisPanel;
